import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilePdf, StackSimple, Scissors, ArrowsClockwise, Trash, ImagesSquare, DownloadSimple, UploadSimple, Compass } from "@phosphor-icons/react";
import { toast } from "sonner";

const TOOLS = [
  { id: "merge",       label: "Merge PDFs",       icon: StackSimple,     desc: "Combine multiple PDFs into one file" },
  { id: "split",       label: "Split PDF",        icon: Scissors,        desc: "Extract selected pages into separate PDFs" },
  { id: "compress",    label: "Compress PDF",     icon: Compass,         desc: "Shrink PDF size using pikepdf" },
  { id: "rotate",      label: "Rotate Pages",     icon: ArrowsClockwise, desc: "Rotate specific pages by 90 / 180 / 270°" },
  { id: "delete",      label: "Delete Pages",     icon: Trash,           desc: "Remove selected pages from a PDF" },
  { id: "image2pdf",   label: "Image → PDF",      icon: ImagesSquare,    desc: "Convert one or more images into a PDF" },
];

export default function PdfStudio() {
  const [tool, setTool] = useState("merge");

  return (
    <div className="max-w-[1500px] mx-auto p-6" data-testid="pdf-studio-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <FilePdf size={16} weight="fill" className="text-red-600" />
          <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500">PDF Tools Studio</span>
        </div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>PDF Studio</h1>
        <p className="text-sm text-slate-500 mt-1">Merge · Split · Compress · Rotate · Delete pages · Image → PDF · All processed server-side with pypdf + pikepdf.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Tool picker */}
        <Card className="lg:col-span-1 p-3 bg-white border-slate-200" data-testid="pdf-tools-list">
          {TOOLS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTool(t.id)}
              data-testid={`pdf-tool-${t.id}`}
              className={`w-full flex items-start gap-3 p-3 rounded-md text-left transition-colors mb-1 ${tool === t.id ? "bg-red-50 border border-red-300" : "hover:bg-slate-50 border border-transparent"}`}
            >
              <t.icon size={20} weight="duotone" className={tool === t.id ? "text-red-600" : "text-slate-500"} />
              <div className="min-w-0">
                <div className={`text-sm font-semibold ${tool === t.id ? "text-red-800" : "text-slate-900"}`}>{t.label}</div>
                <div className="text-[11px] text-slate-500">{t.desc}</div>
              </div>
            </button>
          ))}
        </Card>

        {/* Tool panel */}
        <div className="lg:col-span-3">
          {tool === "merge"     && <MergePanel />}
          {tool === "split"     && <SplitPanel />}
          {tool === "compress"  && <CompressPanel />}
          {tool === "rotate"    && <RotatePanel />}
          {tool === "delete"    && <DeletePanel />}
          {tool === "image2pdf" && <ImageToPdfPanel />}
        </div>
      </div>
    </div>
  );
}

// ============ Merge ============
function MergePanel() {
  const [files, setFiles] = useState([]);  // [{ name, size, b64 }]
  const [busy, setBusy] = useState(false);
  const [output, setOutput] = useState(null);

  const addFiles = async (fileList) => {
    const arr = Array.from(fileList || []);
    const loaded = await Promise.all(arr.map(readFile));
    setFiles((cur) => [...cur, ...loaded.filter(Boolean)]);
  };

  const move = (i, dir) => {
    setFiles((cur) => {
      const c = [...cur];
      const t = c[i + dir]; if (!t) return cur;
      c[i + dir] = c[i]; c[i] = t; return c;
    });
  };
  const remove = (i) => setFiles((c) => c.filter((_, j) => j !== i));

  const run = async () => {
    if (files.length < 2) { toast.error("Add at least 2 PDFs"); return; }
    setBusy(true); setOutput(null);
    try {
      const { data } = await api.post("/pdf/merge", { pdfs: files.map((f) => f.b64) });
      setOutput({ pdf: data.pdf, size: data.output_size, name: `merged-${files.length}-files.pdf` });
      toast.success(`Merged ${files.length} PDFs`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5 bg-white border-slate-200 space-y-4">
      <SectionTitle icon={StackSimple}>Merge PDFs</SectionTitle>
      <FileDrop accept="application/pdf" multiple onFiles={addFiles} testid="pdf-merge-drop" hint="Drop 2 or more PDFs" />
      {files.length > 0 && (
        <div className="space-y-2" data-testid="pdf-merge-list">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-2 border border-slate-200 rounded-md p-2 text-sm">
              <span className="w-6 h-6 rounded bg-slate-100 text-slate-600 text-xs font-bold flex items-center justify-center">{i + 1}</span>
              <span className="flex-1 truncate">{f.name}</span>
              <span className="text-xs text-slate-500">{fmtBytes(f.size)}</span>
              <Button size="icon" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0} className="h-7 w-7 text-xs">↑</Button>
              <Button size="icon" variant="ghost" onClick={() => move(i, +1)} disabled={i === files.length - 1} className="h-7 w-7 text-xs">↓</Button>
              <Button size="icon" variant="ghost" onClick={() => remove(i)} className="h-7 w-7 text-red-600"><Trash size={12} /></Button>
            </div>
          ))}
        </div>
      )}
      <Button onClick={run} disabled={busy || files.length < 2} className="bg-red-600 hover:bg-red-700 text-white gap-2 w-full h-10" data-testid="pdf-merge-run">
        <StackSimple size={14} weight="bold" /> {busy ? "Merging…" : `Merge ${files.length} PDFs`}
      </Button>
      {output && <ResultLink {...output} />}
    </Card>
  );
}

// ============ Split ============
function SplitPanel() {
  const [file, setFile] = useState(null);
  const [ranges, setRanges] = useState("");
  const [busy, setBusy] = useState(false);
  const [parts, setParts] = useState([]);
  const run = async () => {
    if (!file) { toast.error("Pick a PDF"); return; }
    setBusy(true); setParts([]);
    try {
      const { data } = await api.post("/pdf/split", { pdf: file.b64, ranges });
      setParts(data.parts);
      toast.success(`Split into ${data.parts.length} PDFs (source ${data.total_pages} pages)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-5 bg-white border-slate-200 space-y-4">
      <SectionTitle icon={Scissors}>Split PDF</SectionTitle>
      <FileDrop accept="application/pdf" onFiles={(fl) => readFile(fl[0]).then(setFile)} testid="pdf-split-drop" hint="Drop 1 PDF" />
      {file && <div className="text-xs text-slate-600">{file.name} · {fmtBytes(file.size)}</div>}
      <div>
        <Label>Page ranges (leave empty = one file per page)</Label>
        <Input value={ranges} onChange={(e) => setRanges(e.target.value)} placeholder="1-3, 5, 7-9 · each group becomes one PDF" data-testid="pdf-split-ranges" />
      </div>
      <Button onClick={run} disabled={busy || !file} className="bg-red-600 hover:bg-red-700 text-white gap-2 w-full h-10" data-testid="pdf-split-run">
        {busy ? "Splitting…" : "Split"}
      </Button>
      {parts.length > 0 && (
        <div className="space-y-2" data-testid="pdf-split-results">
          {parts.map((p) => (
            <ResultLink key={p.index} pdf={p.pdf} name={`part-${p.index + 1}-p${p.label}.pdf`} sub={`${p.pages} page${p.pages > 1 ? "s" : ""} · ${p.label}`} />
          ))}
        </div>
      )}
    </Card>
  );
}

// ============ Compress ============
function CompressPanel() {
  const [file, setFile] = useState(null);
  const [targetKb, setTargetKb] = useState(0);
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);
  const run = async () => {
    if (!file) return;
    setBusy(true); setOut(null);
    try {
      const { data } = await api.post("/pdf/compress", { pdf: file.b64, target_kb: Number(targetKb) || 0 });
      setOut({ pdf: data.pdf, size: data.output_size, name: `compressed-${file.name}`, original: data.original_size, achieved: data.achieved_target });
      const pct = Math.round((1 - data.output_size / data.original_size) * 100);
      toast.success(pct > 0 ? `${pct}% smaller · ${fmtBytes(data.original_size)} → ${fmtBytes(data.output_size)}` : `Optimized`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-5 bg-white border-slate-200 space-y-4">
      <SectionTitle icon={Compass}>Compress PDF</SectionTitle>
      <FileDrop accept="application/pdf" onFiles={(fl) => readFile(fl[0]).then(setFile)} testid="pdf-compress-drop" hint="Drop 1 PDF" />
      {file && <div className="text-xs text-slate-600">{file.name} · {fmtBytes(file.size)}</div>}
      <div>
        <Label>Target size (KB, 0 = best-effort)</Label>
        <Input type="number" min={0} value={targetKb} onChange={(e) => setTargetKb(e.target.value)} placeholder="e.g. 500" data-testid="pdf-compress-target" />
      </div>
      <Button onClick={run} disabled={busy || !file} className="bg-red-600 hover:bg-red-700 text-white w-full h-10" data-testid="pdf-compress-run">
        {busy ? "Compressing…" : "Compress"}
      </Button>
      {out && <ResultLink {...out} sub={out.achieved ? "Ready" : "Best-effort · could not hit exact target without rasterizing"} />}
    </Card>
  );
}

// ============ Rotate ============
function RotatePanel() {
  const [file, setFile] = useState(null);
  const [pages, setPages] = useState("");
  const [angle, setAngle] = useState("90");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);
  const run = async () => {
    if (!file) return;
    setBusy(true); setOut(null);
    try {
      const { data } = await api.post("/pdf/rotate", { pdf: file.b64, pages, angle: Number(angle) });
      setOut({ pdf: data.pdf, name: `rotated-${file.name}`, sub: `Rotated ${data.rotated_pages.length} page(s)` });
      toast.success("Rotated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-5 bg-white border-slate-200 space-y-4">
      <SectionTitle icon={ArrowsClockwise}>Rotate Pages</SectionTitle>
      <FileDrop accept="application/pdf" onFiles={(fl) => readFile(fl[0]).then(setFile)} testid="pdf-rotate-drop" hint="Drop 1 PDF" />
      {file && <div className="text-xs text-slate-600">{file.name}</div>}
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Pages (empty = all)</Label><Input value={pages} onChange={(e) => setPages(e.target.value)} placeholder="1-3, 5" data-testid="pdf-rotate-pages" /></div>
        <div>
          <Label>Angle</Label>
          <Select value={angle} onValueChange={setAngle}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{["90", "180", "270"].map((a) => <SelectItem key={a} value={a}>{a}°</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <Button onClick={run} disabled={busy || !file} className="bg-red-600 hover:bg-red-700 text-white w-full h-10" data-testid="pdf-rotate-run">
        {busy ? "Rotating…" : "Rotate"}
      </Button>
      {out && <ResultLink {...out} />}
    </Card>
  );
}

// ============ Delete Pages ============
function DeletePanel() {
  const [file, setFile] = useState(null);
  const [pages, setPages] = useState("");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);
  const run = async () => {
    if (!file || !pages.trim()) return;
    setBusy(true); setOut(null);
    try {
      const { data } = await api.post("/pdf/delete-pages", { pdf: file.b64, pages });
      setOut({ pdf: data.pdf, name: `pruned-${file.name}`, sub: `Deleted ${data.deleted.length} · ${data.remaining_pages} pages left` });
      toast.success("Pages deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-5 bg-white border-slate-200 space-y-4">
      <SectionTitle icon={Trash}>Delete Pages</SectionTitle>
      <FileDrop accept="application/pdf" onFiles={(fl) => readFile(fl[0]).then(setFile)} testid="pdf-delete-drop" hint="Drop 1 PDF" />
      {file && <div className="text-xs text-slate-600">{file.name}</div>}
      <div>
        <Label>Pages to delete</Label>
        <Input value={pages} onChange={(e) => setPages(e.target.value)} placeholder="e.g. 2, 4-6" data-testid="pdf-delete-pages" />
      </div>
      <Button onClick={run} disabled={busy || !file || !pages.trim()} className="bg-red-600 hover:bg-red-700 text-white w-full h-10" data-testid="pdf-delete-run">
        {busy ? "Deleting…" : "Delete pages"}
      </Button>
      {out && <ResultLink {...out} />}
    </Card>
  );
}

// ============ Image → PDF ============
function ImageToPdfPanel() {
  const [images, setImages] = useState([]);
  const [pageSize, setPageSize] = useState("A4");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);
  const add = async (fl) => {
    const arr = await Promise.all(Array.from(fl || []).map(readFile));
    setImages((c) => [...c, ...arr.filter(Boolean)]);
  };
  const run = async () => {
    if (!images.length) return;
    setBusy(true); setOut(null);
    try {
      const { data } = await api.post("/pdf/image-to-pdf", { images: images.map((f) => f.b64), page_size: pageSize });
      setOut({ pdf: data.pdf, name: `images-${data.pages}-pages.pdf`, sub: `${data.pages} pages · ${pageSize}` });
      toast.success(`Built ${data.pages}-page PDF`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-5 bg-white border-slate-200 space-y-4">
      <SectionTitle icon={ImagesSquare}>Image → PDF</SectionTitle>
      <FileDrop accept="image/*" multiple onFiles={add} testid="pdf-i2p-drop" hint="Drop images (multiple ok)" />
      {images.length > 0 && (
        <div className="grid grid-cols-4 gap-2" data-testid="pdf-i2p-thumbs">
          {images.map((im, i) => (
            <div key={i} className="relative">
              <img src={im.b64} alt="" className="w-full h-20 object-cover rounded border border-slate-200" />
              <button onClick={() => setImages((c) => c.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">×</button>
            </div>
          ))}
        </div>
      )}
      <div>
        <Label>Page size</Label>
        <Select value={pageSize} onValueChange={setPageSize}>
          <SelectTrigger data-testid="pdf-i2p-size"><SelectValue /></SelectTrigger>
          <SelectContent>{["A4", "A5", "Letter", "Legal", "Original"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Button onClick={run} disabled={busy || !images.length} className="bg-red-600 hover:bg-red-700 text-white w-full h-10" data-testid="pdf-i2p-run">
        {busy ? "Building…" : `Build PDF from ${images.length} image(s)`}
      </Button>
      {out && <ResultLink {...out} />}
    </Card>
  );
}

// ---------- shared ----------
function FileDrop({ accept, multiple, onFiles, testid, hint }) {
  const ref = useRef(null);
  return (
    <div
      onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
      onDragOver={(e) => e.preventDefault()}
      onClick={() => ref.current?.click()}
      data-testid={testid}
      className="border-2 border-dashed border-slate-300 rounded-md p-8 text-center cursor-pointer hover:border-red-400 hover:bg-red-50/40 transition-colors"
    >
      <UploadSimple size={30} weight="duotone" className="mx-auto text-slate-400 mb-2" />
      <div className="text-sm text-slate-600 font-medium">{hint}</div>
      <div className="text-[11px] text-slate-400 mt-1">Click to browse · Drag &amp; drop</div>
      <input ref={ref} type="file" accept={accept} multiple={multiple} className="hidden"
        onChange={(e) => onFiles(e.target.files)} />
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-600 flex items-center gap-1.5">
      <Icon size={11} weight="fill" className="text-red-500" /> {children}
    </div>
  );
}

function ResultLink({ pdf, name, size, sub }) {
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 flex items-center justify-between gap-3" data-testid="pdf-result">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-emerald-900 truncate">{name}</div>
        <div className="text-[11px] text-emerald-700">{sub || (size ? fmtBytes(size) : "")}</div>
      </div>
      <a href={pdf} download={name} className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-xs font-medium px-3 py-1.5 rounded-md hover:bg-emerald-700">
        <DownloadSimple size={12} weight="bold" /> Download
      </a>
    </div>
  );
}

function readFile(file) {
  if (!file) return Promise.resolve(null);
  return new Promise((res) => {
    const r = new FileReader();
    r.onload = () => res({ name: file.name, size: file.size, b64: r.result });
    r.onerror = () => res(null);
    r.readAsDataURL(file);
  });
}

function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(2)} MB`;
}
